import { useState, useEffect } from 'react';
import { useTaxStore } from '../../../store/taxStore';
import { Card, CardHeader, CardContent } from '../../../components/Card';
import { Button } from '../../../components/Button';
import { Badge } from '../../../components/Badge';
import { Plus, Search, FileText, AlertTriangle, CalendarCheck } from 'lucide-react';
import { Input } from '../../../components/Input';
import toast from 'react-hot-toast';

export function TaxCenter() {
    const { records, isLoading, fetchRecords, createRecord } = useTaxStore();
    const [search, setSearch] = useState('');

    useEffect(() => {
        fetchRecords();
    }, []);

    const filteredRecords = records.filter(r => 
        r.taxName.toLowerCase().includes(search.toLowerCase()) ||
        r.jurisdiction.toLowerCase().includes(search.toLowerCase())
    );

    const formatCurrency = (val: number) => 
        new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val);

    const handleCreateMock = async () => {
        try {
            await createRecord({
                taxName: 'Q3 VAT Return',
                taxRate: 20.0,
                amount: Math.floor(Math.random() * 5000) + 500,
                jurisdiction: 'United Kingdom',
                filingStatus: 'pending',
                filingDate: new Date(Date.now() + 15 * 24 * 60 * 60 * 1000).toISOString(),
            });
            toast.success('Tax record created');
        } catch (e) {
            toast.error('Failed to create tax record');
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="tracking-tight text-foreground font-display text-page-title">Tax Center</h1>
                    <p className="text-muted-foreground">Manage tax configurations, filings, and compliance.</p>
                </div>
                <Button variant="primary" onClick={handleCreateMock}>
                    <Plus className="w-4 h-4 mr-2" />
                    New Tax Record
                </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card>
                    <CardContent className="p-4 flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-warning/10 flex items-center justify-center text-warning">
                            <AlertTriangle className="w-5 h-5" />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-muted-foreground">Pending Filings</p>
                            <h3 className="text-foreground font-display text-metric">
                                {records.filter(r => r.filingStatus === 'pending').length}
                            </h3>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-4 flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                            <FileText className="w-5 h-5" />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-muted-foreground">Total Liability</p>
                            <h3 className="text-foreground font-display text-metric">
                                {formatCurrency(records.reduce((acc, r) => r.filingStatus !== 'filed' ? acc + r.amount : acc, 0))}
                            </h3>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-4 flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center text-success">
                            <CalendarCheck className="w-5 h-5" />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-muted-foreground">Filed this Year</p>
                            <h3 className="text-foreground font-display text-metric">
                                {records.filter(r => r.filingStatus === 'filed').length}
                            </h3>
                        </div>
                    </CardContent>
                </Card>
            </div>

            <Card>
                <CardHeader>
                    <div className="flex justify-between items-center">
                        <h3 className="text-foreground font-display text-section-title">Tax Records</h3>
                        <div className="relative w-64">
                            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                            <Input 
                                placeholder="Search tax records..." 
                                className="pl-9"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </div>
                    </div>
                </CardHeader>
                <CardContent className="p-0">
                    {isLoading ? (
                        <div className="p-8 text-center text-muted-foreground">Loading...</div>
                    ) : (
                        <table className="w-full text-sm text-left">
                            <thead className="bg-secondary/50 text-muted-foreground font-semibold border-y border-border">
                                <tr>
                                    <th className="px-6 py-3">Tax Name</th>
                                    <th className="px-6 py-3">Jurisdiction</th>
                                    <th className="px-6 py-3">Rate</th>
                                    <th className="px-6 py-3">Amount</th>
                                    <th className="px-6 py-3">Filing Date</th>
                                    <th className="px-6 py-3">Status</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-border">
                                {filteredRecords.length === 0 ? (
                                    <tr>
                                        <td colSpan={6} className="px-6 py-8 text-center text-muted-foreground">
                                            No tax records found.
                                        </td>
                                    </tr>
                                ) : filteredRecords.map(r => (
                                    <tr key={r.id} className="hover:bg-secondary/20 transition-colors">
                                        <td className="px-6 py-4 font-medium text-foreground">{r.taxName}</td>
                                        <td className="px-6 py-4">{r.jurisdiction}</td>
                                        <td className="px-6 py-4">{r.taxRate}%</td>
                                        <td className="px-6 py-4">{formatCurrency(r.amount)}</td>
                                        <td className="px-6 py-4">
                                            {r.filingDate ? new Date(r.filingDate).toLocaleDateString() : '-'}
                                        </td>
                                        <td className="px-6 py-4">
                                            <Badge variant={r.filingStatus === 'filed' ? 'success' : 'secondary'}>
                                                {r.filingStatus.toUpperCase()}
                                            </Badge>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
